/**
 * KnownEtfTable.jsx — Full-width table for selecting ETFs already in the
 * database. Rendered inside the right SelectionPanel.
 *
 * Phase 36b (UX cleanup, Feb 2026):
 *   • No "expand/collapse" toggle — table is always visible.
 *   • Search bar is full-width inside the panel.
 *   • Bulk "select-visible" removed; replaced by a single
 *     "Deseleziona tutto" action that asks for confirmation.
 *   • "Reset filtri" button clears search / issuer / asset class.
 *   • Asset-class filter is folded into 5 canonical buckets + "Altro".
 *     Anything outside the 5 (Money Market, Cryptocurrency, empty,
 *     unmapped backend taxonomy values, …) lands in "Altro".
 *   • Hard cap on selection: when `selectionLimitReached` is true the
 *     unchecked rows become disabled (the parent decides the cap so
 *     paste-bucket ETFs count too).
 */
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ConfirmDialog, DataTable, FilterBar, FilterSheet, SearchInput, SelectField, Toolbar,
} from "@/components/system";
import { useIsMobile } from "@/hooks/useMediaQuery";

// Detect issuer from ETF name. Curated mapping shared with the admin tab.
const ISSUER_KEYWORDS = [
  ["iShares", /\bishares\b/i],
  ["VanEck",  /\bvaneck\b/i],
  ["Amundi",  /\bamundi\b/i],
  ["Lyxor",   /\blyxor\b/i],
  ["Xtrackers", /\bxtrackers\b/i],
  ["Vanguard", /\bvanguard\b/i],
  ["SPDR",    /\bspdr\b/i],
  ["Invesco", /\binvesco\b/i],
  ["WisdomTree", /\bwisdomtree\b/i],
  ["Global X", /\bglobal\s*x\b/i],
  ["ARK",     /\bark\b/i],
  ["UBS",     /\bubs\b/i],
  ["L&G",     /\bl\s*&\s*g|\blegal\b/i],
];
const issuerOf = (name = "") => {
  for (const [label, re] of ISSUER_KEYWORDS) if (re.test(name)) return label;
  return "Altro";
};

// Canonical asset-class buckets exposed to the user (Phase 36b). Anything
// not in this list — including the backend's "Money Market",
// "Cryptocurrency", "Other / Unknown", empty / unmapped values — is
// rolled up into a single "Altro" bucket.
const VISIBLE_ASSET_BUCKETS = [
  "Equity",
  "Bond",
  "Real Estate",
  "Commodities",
  "Precious Metals",
];
const ALTRO_BUCKET = "Altro";

const bucketAssetClass = (raw) => {
  const v = (raw || "").trim();
  return VISIBLE_ASSET_BUCKETS.includes(v) ? v : ALTRO_BUCKET;
};


export function KnownEtfTable({
  knownEtfs,
  selectedEtfs,
  setSelectedEtfs,
  selectionLimitReached = false,
  onLimitHit,
  disabled = false,
}) {
  const [search, setSearch] = useState("");
  const [issuerFilter, setIssuerFilter] = useState("all");
  const [assetClassFilter, setAssetClassFilter] = useState("all");
  const [confirmDeselect, setConfirmDeselect] = useState(false);

  // Perf (catalogue of ~2.300 rows): issuer regexes, asset bucket and the
  // lowercased haystack are computed ONCE per catalogue, not once per row per
  // keystroke (13 regex x 2.300 rows was the second cost of every search).
  const rows = useMemo(() => knownEtfs.map((e) => ({
    ...e,
    _issuer: issuerOf(e.etf_name),
    _bucket: bucketAssetClass(e.asset_type),
    _hay: `${e.ticker || ""}\n${(e.other_tickers || []).join(" ")}\n${e.isin || ""}\n${e.etf_name || ""}`.toLowerCase(),
  })), [knownEtfs]);

  const issuers = useMemo(() => Array.from(new Set(rows.map((e) => e._issuer))), [rows]);

  // Build the asset-class dropdown options. Always show the canonical
  // 5 buckets in fixed order + "Altro" at the bottom, regardless of
  // which classes are actually present in the data — gives the user a
  // stable mental model.
  const assetClasses = useMemo(
    () => [...VISIBLE_ASSET_BUCKETS, ALTRO_BUCKET],
    [],
  );

  // The input stays on the fast path: the 2.300-row list is re-filtered and
  // re-rendered from the deferred value, so typing never waits for the table.
  const deferredSearch = useDeferredValue(search);

  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    return rows.filter((e) => {
      if (issuerFilter !== "all" && e._issuer !== issuerFilter) return false;
      if (assetClassFilter !== "all" && e._bucket !== assetClassFilter) return false;
      return !q || e._hay.includes(q);
    });
  }, [rows, deferredSearch, issuerFilter, assetClassFilter]);

  const toggle = (isin) => {
    if (disabled) return;
    setSelectedEtfs((prev) => {
      if (prev.includes(isin)) return prev.filter((i) => i !== isin);
      if (selectionLimitReached) {
        // Block the add — parent will surface a banner / toast.
        if (typeof onLimitHit === "function") onLimitHit();
        return prev;
      }
      return [...prev, isin];
    });
  };

  const filtersActive = search !== "" || issuerFilter !== "all" || assetClassFilter !== "all";
  const isMobile = useIsMobile();

  // Perf on phones: rendering 2.300 rows (≈53.000 DOM nodes) made every filter
  // change take seconds on a mid-range device. Below 640px the catalogue is
  // rendered in pages of 150 rows, extended on demand; desktop is unchanged.
  const MOBILE_PAGE = 150;
  const [mobileLimit, setMobileLimit] = useState(MOBILE_PAGE);
  useEffect(() => {
    setMobileLimit(MOBILE_PAGE);
  }, [deferredSearch, issuerFilter, assetClassFilter]);
  const limit = isMobile ? mobileLimit : undefined;
  const hiddenByLimit = limit ? Math.max(0, filtered.length - limit) : 0;

  const resetFilters = () => {
    setSearch("");
    setIssuerFilter("all");
    setAssetClassFilter("all");
  };

  const deselectAll = () => {
    setSelectedEtfs([]);
  };

  const rowIdOf = (etf) => etf.isin || `noisin-${etf.ticker}`;

  const activeFilters = (search !== "" ? 1 : 0) + (issuerFilter !== "all" ? 1 : 0)
    + (assetClassFilter !== "all" ? 1 : 0);

  // The same fields, rendered once: inline on ≥640, inside the sheet below it.
  const filterFields = (
    <>
      <SelectField
        label="Emittente"
        testId="known-etf-issuer-filter"
        value={issuerFilter}
        onChange={setIssuerFilter}
        options={issuers}
        allLabel="Tutti gli emittenti"
        placeholderValue="all"
        className={isMobile ? "w-full" : "w-[200px]"}
      />
      <SelectField
        label="Asset class"
        testId="known-etf-asset-class-filter"
        value={assetClassFilter}
        onChange={setAssetClassFilter}
        options={assetClasses}
        allLabel="Tutte le asset class"
        placeholderValue="all"
        sort={false}
        className={isMobile ? "w-full" : "w-[200px]"}
      />
    </>
  );

  const selectionStatus = (
    <>
      {selectedEtfs.length > 0 && (
        <Badge variant="info" className="text-label">
          {selectedEtfs.length} selezionati
        </Badge>
      )}
      <Button
        variant="ghost" size="sm"
        data-testid="known-etf-deselect-all"
        onClick={() => setConfirmDeselect(true)}
        disabled={disabled || selectedEtfs.length === 0}
        className="text-danger hover:text-danger hover:bg-danger-solid/10 h-10 text-xs disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <X className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
        Deseleziona tutto
      </Button>
    </>
  );

  const columns = useMemo(() => [
    {
      key: "ticker", header: "Ticker", width: 88,
      className: "font-mono font-semibold !text-brand-accent truncate",
    },
    {
      key: "etf_name", header: "Nome ETF",
      accessor: (etf) => etf.etf_name,
      render: (etf) => (
        <span className="block min-w-0">
          {/* Phase 6 — the name wraps instead of being cut to "ARK Genomi…": on
              a 390px viewport it is the only way to tell two ETFs apart. */}
          <span className="block break-words leading-tight !text-content">{etf.etf_name}</span>
          {/* A3.11 — where a low-priority column is hidden, its value stays
              visible here: never behind a native title alone. */}
          <span className="block break-words text-label text-content-muted sm:hidden">
            {etf.isin} · {etf._issuer} · {etf._bucket} ·{" "}
            {etf.holdings_count} holdings
          </span>
          <span className="hidden sm:block lg:hidden break-words text-label text-content-muted">
            {etf.isin} · {etf._issuer} · {etf._bucket}
          </span>
          <span className="hidden lg:block xl:hidden truncate text-label text-content-muted">
            {etf.isin}
          </span>
        </span>
      ),
      title: (etf) => `${etf.etf_name} — ${etf._issuer} · ${etf._bucket}`,
    },
    {
      key: "isin", header: "ISIN", width: 132,
      className: "font-mono truncate hidden xl:table-cell",
      headerClassName: "hidden xl:table-cell",
    },
    {
      key: "issuer", header: "Emittente", width: 116,
      className: "truncate hidden lg:table-cell",
      headerClassName: "hidden lg:table-cell",
      accessor: (etf) => etf._issuer,
      render: (etf) => etf._issuer,
    },
    {
      key: "asset_class", header: "Asset class", width: 116,
      className: "truncate hidden lg:table-cell",
      headerClassName: "hidden lg:table-cell",
      accessor: (etf) => etf._bucket,
      render: (etf) => etf._bucket,
    },
    {
      key: "holdings_count", header: "Holdings", numeric: true, width: 108,
      className: "hidden sm:table-cell",
      headerClassName: "hidden sm:table-cell",
    },
  ], []);

  if (knownEtfs.length === 0) return null;

  return (
    <div
      data-testid="known-etf-table"
      className="flex flex-col flex-1 min-h-0"
    >
      {/* Follow-up: search, filters and the destructive action share one band —
          the catalogue is full-width now, so no row is spent on a single button.
          Phase 6: below 640px the secondary filters move into a FilterSheet so
          they don't consume half of the viewport before the first row. */}
      <Toolbar className="mb-2">
        <SearchInput
          testId="known-etf-search"
          value={search}
          onChange={setSearch}
          placeholder="Cerca per ticker, ISIN o nome…"
          label="Cerca un ETF per ticker, ISIN o nome"
        />
        {isMobile && (
          <FilterSheet activeCount={activeFilters} onReset={resetFilters} testId="known-etf-filters">
            {filterFields}
          </FilterSheet>
        )}
      </Toolbar>

      {isMobile ? (
        <div className="flex flex-wrap items-center gap-2 mb-3">{selectionStatus}</div>
      ) : (
        <FilterBar
          className="mb-3"
          activeCount={activeFilters}
          onReset={resetFilters}
          status={selectionStatus}
        >
          {filterFields}
        </FilterBar>
      )}

      <DataTable
        testId="known-etf-datatable"
        columns={columns}
        rows={filtered}
        limit={limit}
        density="compact"
        fitWidth
        totalCount={knownEtfs.length}
        countLabel="ETF"
        caption="ETF già presenti nel database"
        maxHeight="min(58vh, 620px)"
        getRowId={rowIdOf}
        rowTestId="known-etf"
        selectable
        isRowSelected={(etf) => Boolean(etf.isin && selectedEtfs.includes(etf.isin))}
        onToggleRow={(etf) => {
          const sel = etf.isin && selectedEtfs.includes(etf.isin);
          if (disabled || (!sel && selectionLimitReached && !sel)) {
            if (!disabled && !sel && selectionLimitReached && typeof onLimitHit === "function") {
              onLimitHit();
            }
            return;
          }
          if (etf.isin) toggle(etf.isin);
        }}
        rowClassName={(etf) => {
          const sel = etf.isin && selectedEtfs.includes(etf.isin);
          const base = "known-etf-row";
          return disabled || (!sel && selectionLimitReached)
            ? `${base} opacity-40 cursor-not-allowed`
            : base;
        }}
        emptyTitle="Nessun ETF corrispondente"
        emptyDescription="Modifica la ricerca o azzera i filtri per vedere altri ETF."
      />

      {hiddenByLimit > 0 && (
        <div className="mt-2 flex flex-col items-stretch gap-1">
          <Button
            variant="outline"
            data-testid="known-etf-load-more"
            onClick={() => setMobileLimit((n) => n + MOBILE_PAGE)}
            className="h-10 text-xs"
          >
            <ChevronDown className="w-4 h-4 mr-1" aria-hidden="true" />
            Mostra altri {Math.min(MOBILE_PAGE, hiddenByLimit)} ETF
          </Button>
          <p className="text-label text-content-muted text-center" data-testid="known-etf-load-more-hint">
            Altri {hiddenByLimit} ETF disponibili: usa la ricerca per trovarne uno subito.
          </p>
        </div>
      )}

      <ConfirmDialog
        open={confirmDeselect}
        onOpenChange={setConfirmDeselect}
        testId="known-etf-deselect-confirm"
        variant="destructive"
        title="Deselezionare tutti gli ETF?"
        description={`Stai per deselezionare ${selectedEtfs.length} ETF dal portafoglio.`}
        consequence="L'azione rimuove anche gli ETF importati via ISIN."
        confirmLabel="Deseleziona tutto"
        onConfirm={deselectAll}
      />
    </div>
  );
}
